import {
  GITHUB_FIRECRAWL_URL,
  GITHUB_MCP_URL,
  GITHUB_REDDIT_SCRAPER_URL,
  GITHUB_X_SCRAPER_URL,
} from "@/lib/constants";

export interface OpenSourceProject {
  id: string;
  name: string;
  tagline: string;
  description: string;
  language: string;
  license: string;
  githubUrl: string;
  installCommand: string;
  quickStartCommand: string;
  features: string[];
  blogUrl?: string;
  /** Internal page link with a custom label — for projects with a dedicated
   *  product page rather than a blog post. */
  pageUrl?: string;
  pageLabel?: string;
}

export const projects: OpenSourceProject[] = [
  {
    id: "reddit-scraper",
    name: "Reddit Scraper",
    tagline: "Scrape any subreddit, user, or post — no API key needed.",
    description:
      "A production-ready Python scraper for Reddit built around the public JSON endpoints. Supports proxy rotation, captcha solving, rate limiting, and full comment thread extraction out of the box.",
    language: "Python",
    license: "MIT",
    githubUrl: GITHUB_REDDIT_SCRAPER_URL,
    installCommand: "uv venv && uv pip install -e .",
    quickStartCommand: "python3 -m reddit_scraper.cli interactive",
    features: [
      "JSON endpoint scraping — no Reddit API key required",
      "Automatic proxy rotation with health monitoring",
      "Captcha solving via Capsolver integration",
      "Full comment thread extraction with nested replies",
      "JSON and CSV export with standardised field names",
      "Docker support and rich CLI with progress bars",
    ],
    blogUrl: "/blog/reddit-scraper",
  },
  {
    id: "x-scraper",
    name: "X / Twitter Scraper",
    tagline: "Scrape timelines, keywords, and date ranges — no official API needed.",
    description:
      "A Python scraper for X (Twitter) powered by Playwright for browser automation and OpenAI for AI-powered tweet analysis. Collect tweets, engagement metrics, and media without the official API.",
    language: "Python",
    license: "MIT",
    githubUrl: GITHUB_X_SCRAPER_URL,
    installCommand: "uv sync && playwright install chromium",
    quickStartCommand: "python main.py interactive",
    features: [
      "Timeline, keyword, and historical date-range scraping",
      "AI sentiment analysis, topic extraction, and summaries via OpenAI",
      "Checkpoint and resume for interrupted scrapes",
      "Proxy support for IP rotation with residential and mobile proxies",
      "Cookie-based session persistence for reliable long-term scraping",
      "Rich metadata: engagement metrics, media, hashtags, and URLs",
    ],
    blogUrl: "/blog/twitter-scraper/",
  },
  {
    id: "mcp-server",
    name: "Proxidize MCP Server",
    tagline: "Manage your proxies from Claude, Cursor, and any MCP client.",
    description:
      "The official Proxidize MCP (Model Context Protocol) server. Gives AI agents direct access to your proxy infrastructure — rotate IPs, switch locations, monitor usage, and manage access, all through natural language.",
    language: "TypeScript",
    license: "MIT",
    githubUrl: GITHUB_MCP_URL,
    installCommand: "npx -y @proxidize/mcp",
    quickStartCommand:
      "claude mcp add proxidize -e PROXIDIZE_API_TOKEN=YOUR_TOKEN -- npx -y @proxidize/mcp",
    features: [
      "37 tools across account, proxy, mobile, residential, and analytics",
      "Smart tool discovery — registers only what your subscription supports",
      "Works with Claude Code, Claude Desktop, Cursor, Windsurf, and VS Code",
      "One command to install, no build step required",
      "Built-in usage analytics — bandwidth, top domains, connection history",
    ],
    pageUrl: "/tools/mcp-server",
    pageLabel: "Explore the MCP server",
  },
  {
    id: "firecrawl-proxidize",
    name: "Firecrawl + Proxidize",
    tagline: "Run self-hosted Firecrawl through Proxidize residential or mobile proxies.",
    description:
      "Configuration, scripts, and docs for running self-hosted Firecrawl through a Proxidize proxy. Clones the official Firecrawl repo and layers on a tested .env template, start-up scripts, and a verification script that confirms your traffic is actually routed through Proxidize.",
    language: "Shell",
    license: "MIT",
    githubUrl: GITHUB_FIRECRAWL_URL,
    installCommand: "git clone https://github.com/proxidize/firecrawl-proxidize.git",
    quickStartCommand: "./scripts/check-requirements.sh && ./scripts/start-firecrawl.sh",
    features: [
      "Tested .env template with the Proxidize variables Firecrawl actually supports",
      "Verifies your traffic is really routed through Proxidize, not just assumed",
      "Residential, mobile, sticky-session, and geo-targeting configuration docs",
      "Scripts to start Firecrawl, run a test scrape/crawl, and check requirements",
      "Always works against a fresh clone of the official Firecrawl repo, never a stale fork",
    ],
    blogUrl: "/blog/firecrawl-self-host-proxies/",
  },
];
